/**
 * Mensajes (`/mensajes`).
 *
 * Portado de `PortalCliente::mensajes()` + `PortalCliente_model`:
 * - El cliente escribe consultas, reclamos o solicitudes (`tipo_mensaje`).
 * - Las respuestas de la estación llegan como mensajes hijos
 *   (`message_padre_id`) y quedan como «sin leer» hasta que se abren aquí: al
 *   abrir el hilo se marca `fecha_lectura` (igual que `api_marcar_leido`).
 *
 * La bandeja desde la que el administrador responde está en el panel
 * (`/gestion/clientes/mensajes`).
 */

import { Check, MailOpen, MessageSquarePlus, RotateCcw, Search, Send } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Buscador } from '@/components/ui/buscador';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { EstadoVacio } from '@/components/ui/estado-vacio';
import { Input } from '@/components/ui/input';
import { Paginador } from '@/components/ui/paginador';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { TituloPagina } from '@/components/ui/titulo-pagina';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { coincideBusqueda } from '@/lib/buscar';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatDateTimeValue } from '@/lib/utils';

import { useEnviarMensaje, useMarcarMensajeLeido, usePortalMensajes } from '../portal.hooks';
import type { MensajeFormulario, MensajePortal } from '../portal.types';

/** Formulario nuevo (por defecto, una consulta de prioridad media). */
const VACIO: MensajeFormulario = { asunto: '', mensaje: '', tipo: 'consulta', prioridad: 'media' };

const ETIQUETA_TIPO: Record<MensajePortal['tipo'], string> = {
  consulta: 'Consulta',
  reclamo: 'Reclamo',
  solicitud: 'Solicitud',
  respuesta: 'Respuesta',
};

const ETIQUETA_ESTADO: Record<MensajePortal['estado'], string> = {
  pendiente: 'Pendiente',
  leido: 'Leído',
  respondido: 'Respondido',
  cerrado: 'Cerrado',
};

/** Mensaje legible de un error de la API. */
function mensajeDe(error: unknown, porDefecto: string): string {
  return error instanceof ApiError ? error.message : porDefecto;
}

/** Color de la etiqueta de prioridad. */
function tonoPrioridad(prioridad: MensajePortal['prioridad']): 'danger' | 'warning' | 'neutral' {
  if (prioridad === 'alta') return 'danger';
  if (prioridad === 'media') return 'warning';

  return 'neutral';
}

/** Color de la etiqueta de estado. */
function tonoEstado(estado: MensajePortal['estado']): 'success' | 'brand' | 'neutral' | 'warning' {
  if (estado === 'respondido') return 'success';
  if (estado === 'cerrado') return 'neutral';
  if (estado === 'leido') return 'brand';

  return 'warning';
}

/** Hilo: mensaje del cliente + las respuestas de la estación. */
function Hilo({ raiz, respuestas }: { raiz: MensajePortal; respuestas: MensajePortal[] }) {
  const [abierto, setAbierto] = useState(false);
  const marcar = useMarcarMensajeLeido();
  const marcados = useRef(new Set<number>());

  const sinLeer = respuestas.filter((item) => item.leido_en === null);

  // Al abrir el hilo se dan por leídas las respuestas que aún no lo estaban
  // (`api_marcar_leido` del portal anterior). El `Set` evita repetir la llamada
  // por cada re-render.
  useEffect(() => {
    if (!abierto) return;

    for (const respuesta of sinLeer) {
      if (marcados.current.has(respuesta.id)) continue;

      marcados.current.add(respuesta.id);
      marcar.mutate(respuesta.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sólo al abrir o al llegar respuestas nuevas
  }, [abierto, sinLeer.length]);

  return (
    <li className="rounded-card border border-ink-100 p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ink-900">
            {raiz.asunto}
            {sinLeer.length > 0 ? <Badge variant="brand">{sinLeer.length} sin leer</Badge> : null}
          </p>
          <p className="mt-0.5 text-xs text-ink-500">
            {ETIQUETA_TIPO[raiz.tipo]} · {formatDateTimeValue(raiz.fecha)}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={tonoPrioridad(raiz.prioridad)}>{raiz.prioridad}</Badge>
          <Badge variant={tonoEstado(raiz.estado)}>{ETIQUETA_ESTADO[raiz.estado]}</Badge>
          <Button variant="ghost" size="sm" onClick={() => setAbierto(!abierto)}>
            {abierto ? 'Ocultar' : respuestas.length > 0 ? `Ver respuestas (${respuestas.length})` : 'Ver detalle'}
          </Button>
        </div>
      </div>

      {abierto ? (
        <div className="mt-3 flex flex-col gap-2">
          <div className="rounded-control bg-ink-50/70 p-3">
            <p className="text-[11px] font-semibold tracking-wide text-ink-400 uppercase">Tu mensaje</p>
            <p className="mt-1 text-sm whitespace-pre-line text-ink-700">{raiz.mensaje}</p>
          </div>

          {respuestas.map((respuesta) => (
            <div
              key={respuesta.id}
              className="rounded-control border border-emerald-100 bg-emerald-50/60 p-3"
            >
              <p className="flex items-center gap-2 text-[11px] font-semibold tracking-wide text-emerald-700 uppercase">
                Respuesta de la estación
                {respuesta.leido_en === null ? <Badge variant="brand">nuevo</Badge> : null}
              </p>
              <p className="mt-1 text-sm whitespace-pre-line text-ink-800">{respuesta.mensaje}</p>
              <p className="mt-1 text-[11px] text-ink-400">{formatDateTimeValue(respuesta.fecha)}</p>
            </div>
          ))}

          {respuestas.length === 0 ? (
            <p className="flex items-center gap-2 text-xs text-ink-500">
              <MailOpen className="size-3.5" aria-hidden="true" />
              Aún no hay respuesta. Te avisaremos aquí mismo.
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

export function PortalMensajesPage() {
  useDocumentTitle('Portal · Mensajes');
  const consulta = usePortalMensajes();
  const enviar = useEnviarMensaje();

  const [formulario, setFormulario] = useState<MensajeFormulario | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState('');

  const mensajes = consulta.data?.mensajes;

  const hilos = useMemo(
    () =>
      (mensajes ?? [])
        .filter((item) => item.padre_id === null)
        .map((raiz) => ({
          raiz,
          respuestas: (mensajes ?? [])
            .filter((item) => item.padre_id === raiz.id)
            .sort((a, b) => String(a.fecha).localeCompare(String(b.fecha))),
        })),
    [mensajes],
  );

  const hilosFiltrados = useMemo(
    () =>
      hilos.filter(({ raiz, respuestas }) =>
        coincideBusqueda(
          [
            raiz.asunto,
            raiz.mensaje,
            ETIQUETA_TIPO[raiz.tipo],
            ETIQUETA_ESTADO[raiz.estado],
            raiz.prioridad,
            formatDateTimeValue(raiz.fecha),
            ...respuestas.map((respuesta) => respuesta.mensaje),
          ],
          busqueda,
        ),
      ),
    [hilos, busqueda],
  );

  const paginacion = usePaginacion(hilosFiltrados, { clave: busqueda });

  const mostrar = (mensaje: string) => {
    setAviso(mensaje);
    setTimeout(() => setAviso(null), 6000);
  };

  const confirmarEnvio = () => {
    if (!formulario) return;

    enviar.mutate(formulario, {
      onSuccess: () => {
        mostrar('Mensaje enviado. La estación lo verá en su bandeja.');
        setFormulario(null);
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <TituloPagina
          icono={MailOpen}
          titulo="Mensajes"
          descripcion="Escribe a la estación y revisa sus respuestas sobre tus consumos, facturas o créditos."
        />
        <Button
          size="sm"
          leftIcon={<MessageSquarePlus className="size-3.5" aria-hidden="true" />}
          onClick={() => setFormulario({ ...VACIO })}
        >
          Nuevo mensaje
        </Button>
      </div>

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      {consulta.error ? (
        <Alert variant="error">
          {mensajeDe(consulta.error, 'No se pudieron cargar tus mensajes.')}
        </Alert>
      ) : null}

      <SectionCard
        title={`Mis mensajes (${hilos.length})`}
        description={
          consulta.data && consulta.data.sin_leer > 0
            ? `${consulta.data.sin_leer} respuesta(s) sin leer`
            : 'Al día'
        }
      >
        <div className="flex flex-col gap-3">
          <Buscador
            valor={busqueda}
            onChange={setBusqueda}
            ariaLabel="Buscar en los mensajes"
            placeholder="Buscar asunto, texto o estado…"
          />

          {busqueda.trim() ? (
            <p className="text-xs text-ink-500">
              <strong className="font-semibold text-ink-800">{hilosFiltrados.length}</strong> de{' '}
              {hilos.length} hilos coinciden con{' '}
              <strong className="font-semibold text-ink-800">“{busqueda.trim()}”</strong>
            </p>
          ) : null}

          {consulta.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : hilos.length === 0 ? (
            <EstadoVacio
              icon={MailOpen}
              titulo="Aún no has escrito a la estación"
              detalle="Cuéntanos qué necesitas y te responderemos aquí mismo."
              accion={
                <Button
                  size="sm"
                  leftIcon={<MessageSquarePlus className="size-3.5" aria-hidden="true" />}
                  onClick={() => setFormulario({ ...VACIO })}
                >
                  Nuevo mensaje
                </Button>
              }
            />
          ) : hilosFiltrados.length === 0 ? (
            <EstadoVacio
              icon={Search}
              titulo="Sin coincidencias"
              detalle={`No encontramos mensajes para “${busqueda.trim()}”.`}
              accion={
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<RotateCcw className="size-3.5" aria-hidden="true" />}
                  onClick={() => setBusqueda('')}
                >
                  Quitar búsqueda
                </Button>
              }
            />
          ) : (
            <ul className="flex flex-col gap-3">
              {paginacion.visibles.map(({ raiz, respuestas }) => (
                <Hilo key={raiz.id} raiz={raiz} respuestas={respuestas} />
              ))}
            </ul>
          )}
        </div>

        <Paginador {...paginacion} etiqueta="hilos" className="-mx-5 -mb-5 mt-3 rounded-b-card" />
      </SectionCard>

      <Dialog
        open={formulario !== null}
        onClose={() => setFormulario(null)}
        title="Nuevo mensaje"
        description="Cuéntanos qué necesitas y la estación te responderá en este mismo hilo."
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setFormulario(null)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              leftIcon={<Send className="size-3.5" aria-hidden="true" />}
              loading={enviar.isPending}
              onClick={confirmarEnvio}
            >
              Enviar
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="portal-mensaje-tipo" className="text-sm font-medium text-ink-700">
                Motivo
              </label>
              <Select
                id="portal-mensaje-tipo"
                value={formulario?.tipo ?? 'consulta'}
                onChange={(evento) =>
                  setFormulario((previo) => ({
                    ...(previo ?? VACIO),
                    tipo: evento.target.value as MensajeFormulario['tipo'],
                  }))
                }
              >
                <option value="consulta">Consulta</option>
                <option value="reclamo">Reclamo</option>
                <option value="solicitud">Solicitud</option>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="portal-mensaje-prioridad" className="text-sm font-medium text-ink-700">
                Prioridad
              </label>
              <Select
                id="portal-mensaje-prioridad"
                value={formulario?.prioridad ?? 'media'}
                onChange={(evento) =>
                  setFormulario((previo) => ({
                    ...(previo ?? VACIO),
                    prioridad: evento.target.value as MensajeFormulario['prioridad'],
                  }))
                }
              >
                <option value="baja">Baja</option>
                <option value="media">Media</option>
                <option value="alta">Alta</option>
              </Select>
            </div>
          </div>

          <Input
            label="Asunto"
            value={formulario?.asunto ?? ''}
            placeholder="Ej. Consulta por la factura FA03-234"
            onChange={(evento) =>
              setFormulario((previo) => ({ ...(previo ?? VACIO), asunto: evento.target.value }))
            }
          />

          <label className="flex flex-col gap-1.5 text-sm font-medium text-ink-700">
            Mensaje
            <textarea
              rows={5}
              value={formulario?.mensaje ?? ''}
              onChange={(evento) =>
                setFormulario((previo) => ({ ...(previo ?? VACIO), mensaje: evento.target.value }))
              }
              className={cn(
                'w-full rounded-control border border-ink-200 bg-white px-4 py-2.5 text-sm text-ink-900 transition-all',
                'placeholder:text-ink-300 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none',
              )}
            />
          </label>

          {enviar.error ? (
            <Alert variant="error">{mensajeDe(enviar.error, 'No se pudo enviar el mensaje.')}</Alert>
          ) : null}

          <p className="flex items-center gap-1.5 text-[11px] text-ink-400">
            <Check className="size-3" aria-hidden="true" />
            La estación recibirá tu mensaje en su bandeja del panel.
          </p>
        </div>
      </Dialog>
    </div>
  );
}
